import { Collapsible } from '@base-ui/react/collapsible'
import { useMemo, useState } from 'react'
import { threadCopy } from '@/copy'
import { formatDuration } from '@/utils/time'
import { Button } from '../Button/Button'
import { CodeBlock } from '../CodeBlock/CodeBlock'
import { cx } from '../cx'
import { DisclosureChevron } from '../Disclosure/Disclosure'
import { Spinner } from '../Loading/Loading'
import { Text } from '../layout/Text'
import { StatusDot } from '../Status/Status'
import styles from './ToolCall.module.css'

type ToolCallState = 'running' | 'ok' | 'failed'

export type ToolCallProps = {
  name: string
  args?: unknown
  result?: unknown
  durationMs?: number
  state: ToolCallState
  defaultOpen?: boolean
  className?: string
}

const TONE = { running: 'info', ok: 'success', failed: 'danger' } as const

const ARGS_LINES = 12

function text(v: unknown): string {
  if (v === undefined || v === null) return ''
  if (typeof v === 'string') return v
  try {
    return JSON.stringify(v, null, 2)
  } catch {
    return String(v)
  }
}

export function ToolCall({
  name,
  args,
  result,
  durationMs,
  state,
  defaultOpen,
  className,
}: ToolCallProps) {
  const [open, setOpen] = useState(defaultOpen ?? state === 'failed')
  const [previousState, setPreviousState] = useState(state)
  const [argsOpen, setArgsOpen] = useState(false)

  // A failure opens itself: adjust state while rendering, no effect.
  if (state !== previousState) {
    setPreviousState(state)
    if (state === 'failed') setOpen(true)
  }

  // A streaming turn re-renders per chunk; serialise only when the data changes.
  const argsText = useMemo(() => text(args), [args])
  const resultText = useMemo(() => text(result), [result])
  const hasArgs = argsText !== '' && argsText !== '{}' && argsText !== '[]'
  const hasResult = resultText !== ''
  const hasDetail = hasArgs || hasResult

  const word = threadCopy.toolCall.state[state]
  const lines = argsText.split('\n').length

  const end =
    state === 'running' ? (
      <span className={styles.end}>
        <Spinner tone="current" />
      </span>
    ) : durationMs != null ? (
      <span className={styles.end}>{formatDuration(durationMs)}</span>
    ) : null

  const dot = <StatusDot tone={TONE[state]} label={word} />
  const nameNode = <span className={styles.name}>{name}</span>

  if (!hasDetail) {
    return (
      <div className={cx(styles.toolCall, className)} data-state={state}>
        <div className={styles.summary}>
          <span className={styles.chevronSpace} aria-hidden="true" />
          {dot}
          {nameNode}
          {end}
        </div>
      </div>
    )
  }

  return (
    <div className={cx(styles.toolCall, className)} data-state={state}>
      <Collapsible.Root open={open} onOpenChange={(next) => setOpen(next)}>
        <Collapsible.Trigger className={cx(styles.summary, styles.trigger)}>
          <DisclosureChevron open={open} />
          {dot}
          {nameNode}
          {end}
        </Collapsible.Trigger>
        <Collapsible.Panel className={styles.panel}>
          {hasArgs && (
            <div className={styles.part}>
              <Text variant="label" tone="text-ghost">
                {threadCopy.toolCall.arguments}
              </Text>
              <CodeBlock
                title={name}
                caption={threadCopy.toolCall.argumentsCaption}
                maxLines={argsOpen ? lines : ARGS_LINES}
              >
                {argsText}
              </CodeBlock>
              {lines > ARGS_LINES && (
                <Button
                  variant="quiet"
                  aria-expanded={argsOpen}
                  onClick={() => setArgsOpen((v) => !v)}
                >
                  {argsOpen
                    ? threadCopy.toolCall.showFewer
                    : threadCopy.toolCall.showAll(lines)}
                </Button>
              )}
            </div>
          )}
          {hasResult && (
            <div className={styles.part}>
              <Text variant="label" tone="text-ghost">
                {threadCopy.toolCall.result}
              </Text>
              <CodeBlock
                title={name}
                caption={threadCopy.toolCall.resultCaption}
              >
                {resultText}
              </CodeBlock>
            </div>
          )}
        </Collapsible.Panel>
      </Collapsible.Root>
    </div>
  )
}
